// src/lib/coordinateGrid/shapeCalculations.ts
/**
 * Shape Calculations Library
 * 
 * Mathematical functions for shape metrics.
 * Phase 3: Points and Basic Shapes
 */

import { Point2D } from '../../types/coordinateGrid';

/**
 * Calculate the area of a polygon using the Shoelace formula
 * 
 * Formula: A = 0.5 * |Σ(x_i * y_{i+1} - x_{i+1} * y_i)|
 * 
 * @param vertices Ordered vertices of the polygon
 * @returns Area of the polygon (always positive)
 */
export function calculateArea(vertices: Point2D[]): number {
  if (vertices.length < 3) {
    return 0;
  }

  let sum = 0;
  const n = vertices.length;

  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    sum += vertices[i].x * vertices[j].y;
    sum -= vertices[j].x * vertices[i].y;
  }

  return Math.abs(sum) / 2;
}

/**
 * Calculate the perimeter of a polygon
 * 
 * @param vertices Ordered vertices of the polygon
 * @returns Total perimeter length
 */
export function calculatePerimeter(vertices: Point2D[]): number {
  if (vertices.length < 2) {
    return 0;
  }

  let perimeter = 0;
  const n = vertices.length;

  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    perimeter += calculateDistance(vertices[i], vertices[j]);
  }

  return perimeter;
}

/**
 * Calculate Euclidean distance between two points
 * 
 * @param p1 First point
 * @param p2 Second point
 * @returns Distance between the points
 */
export function calculateDistance(p1: Point2D, p2: Point2D): number {
  return Math.sqrt(Math.pow(p2.x - p1.x, 2) + Math.pow(p2.y - p1.y, 2));
}

/**
 * Calculate the midpoint between two points
 * 
 * @param p1 First point
 * @param p2 Second point
 * @returns Midpoint coordinates
 */
export function calculateMidpoint(p1: Point2D, p2: Point2D): Point2D {
  return {
    x: (p1.x + p2.x) / 2,
    y: (p1.y + p2.y) / 2,
  };
}

/**
 * Calculate the centroid of a polygon
 * 
 * The centroid is the average of all vertex coordinates.
 * 
 * @param vertices Vertices of the polygon
 * @returns Centroid coordinates
 */
export function calculateCentroid(vertices: Point2D[]): Point2D {
  if (vertices.length === 0) {
    return { x: 0, y: 0 };
  }

  const sum = vertices.reduce(
    (acc, vertex) => ({
      x: acc.x + vertex.x,
      y: acc.y + vertex.y,
    }),
    { x: 0, y: 0 }
  );

  return {
    x: sum.x / vertices.length,
    y: sum.y / vertices.length,
  };
}

/**
 * Check if a point is inside a polygon using the ray casting algorithm
 * 
 * @param point Point to test
 * @param vertices Vertices of the polygon (ordered)
 * @returns True if point is inside the polygon
 */
export function isPointInPolygon(point: Point2D, vertices: Point2D[]): boolean {
  if (vertices.length < 3) {
    return false;
  }

  let inside = false;
  const n = vertices.length;

  for (let i = 0, j = n - 1; i < n; j = i++) {
    const xi = vertices[i].x;
    const yi = vertices[i].y;
    const xj = vertices[j].x;
    const yj = vertices[j].y;

    const intersect =
      yi > point.y !== yj > point.y &&
      point.x < ((xj - xi) * (point.y - yi)) / (yj - yi) + xi;

    if (intersect) {
      inside = !inside;
    }
  }

  return inside;
}

/**
 * Snap a point to the nearest grid intersection
 * 
 * @param point Point to snap
 * @param gridInterval Grid interval (e.g., 1, 0.5, 0.25)
 * @returns Snapped point coordinates
 */
export function snapToGrid(point: Point2D, gridInterval: number): Point2D {
  return {
    x: Math.round(point.x / gridInterval) * gridInterval,
    y: Math.round(point.y / gridInterval) * gridInterval,
  };
}



